import type { TimeSeriesDataPoint } from "../types"

/** Accessor for the x (timestamp) field of a TimeSeriesDataPoint. */
export const getTimeX = (d?: TimeSeriesDataPoint): number => d?.x ?? 0

/** Accessor for the y (value) field of a TimeSeriesDataPoint. */
export const getTimeY = (d?: TimeSeriesDataPoint): number => d?.y ?? 0

/**
 * Returns [min, max] for a dataset using the given accessor.
 * Returns [0, 0] for empty data.
 */
export function getExtent<T>(data: T[], accessor: (d: T) => number): [number, number] {
    if (data.length === 0) return [0, 0]
    let min = accessor(data[0])
    let max = accessor(data[0])
    for (const d of data) {
        const val = accessor(d)
        if (val < min) min = val
        if (val > max) max = val
    }
    return [min, max]
}

/**
 * Returns the maximum value in a dataset using the given accessor.
 * Returns 0 for empty data.
 */
export function getMax<T>(data: T[], accessor: (d: T) => number): number {
    if (data.length === 0) return 0
    let max = accessor(data[0])
    for (const d of data) {
        const val = accessor(d)
        if (val > max) max = val
    }
    return max
}

/**
 * The bucket granularity a time-series chart labels its axis by. Kept
 * structurally identical to `@nova/queryable`'s `BucketUnit`, which owns the
 * boundary arithmetic; this package only needs it to pick a date format.
 */
export type BucketUnit = "hour" | "day" | "week" | "month"

/**
 * Binary search: returns the index of the first element whose accessor value
 * is >= x. Used to find the nearest data point to a cursor position.
 */
export function getBisector<T>(data: T[], x: number, accessor: (d: T) => number): number {
    let low = 0
    let high = data.length
    while (low < high) {
        const mid = (low + high) >>> 1
        if (accessor(data[mid]) < x) low = mid + 1
        else high = mid
    }
    return low
}
